<template>
  <BaseWrapper :loading="loading">
    <div class="space-y-4">
      <BaseCard title="Kirim Stok Promo">
        <div :class="['grid gap-4 grid-cols-2']">
          <SearchOfficeInput with-address :office-type="officeType" use-custom-address use-backup-upline-address
            v-model="form.office" @change="onChangeOffice" />
          <BaseInput inset :label="`Nama ${officeTypeLabel}`" disabled v-model="form.office.name"
            :placeholder="officeTypeLabel" />
          <SearchWarehouseInput :disabled="useOriginWarehouse" :with-area="false" v-model="form.warehouse" />
          <BaseInput inset label="Nama Gudang" disabled v-model="form.warehouse.name" placeholder="NASA" />

          <div v-if="form.office.id && form.warehouse.id" class="col-span-full space-y-4">
            <BaseInput label="Metode Pengiriman">
              <div class="flex gap-x-6">
                <BaseRadio val="pickup" v-model="form.order_shipment" label="Diambil" />
                <BaseRadio val="delivery" v-model="form.order_shipment" label="Dikirim" />
              </div>
            </BaseInput>
            <CreateOrderShipmentAddress v-if="form.order_shipment === 'delivery'"
              :shipment-address="form.shipmentAddress" with-address-receipt :office="form.office"
              :custom="form.customShipmentAddress" :select-address-visible="selectAddressModalVisible"
              :create-address-visible="customAddressModalVisible" with-action
              @open-select-address="selectAddressModalVisible = true"
              @close-select-address="selectAddressModalVisible = false"
              @open-create-address="customAddressModalVisible = true"
              @close-create-address="customAddressModalVisible = false" @restore-address="onResetShipmentAddress"
              @select-address="onSelectAddress" @create-address="onChangeShipmentAddress" />

            <BaseInput inset label="Catatan" tag="textarea" rows="3" placeholder="Masukkan catatan"
              v-model="form.notes" />
          </div>
        </div>
      </BaseCard>

      <template v-if="form.office.id && form.warehouse.id">
        <BaseCard title="Barang">
          <template #action>
            <BaseInput placeholder="Cari Kode Barang" :shadow="false" type="text" v-model="search" />
          </template>
          <Datatable :columns="[
            {
              id: 'product_code',
              name: 'Kode Produk',
            },
            {
              id: 'product_name',
              name: 'Nama Produk',
            },
            {
              id: 'min_qty',
              name: 'Jumlah',
            },
            {
              id: 'stock',
              name: 'Stok',
            },
          ]">
            <template #tbody="{ classes }">
              <tr v-for="stock in displayStocks" :key="stock.id">
                <td :class="classes.td">{{ stock.attributes.product_code }}</td>
                <td :class="classes.td">{{ stock.attributes.product_name }}</td>
                <td :class="classes.td">
                  <BaseInput type="text" currency :shadow="false" v-model="stockShipQty[stock.id].qty"
                    @change="onChangeShipQty(stock.id)" />
                </td>
                <td :class="classes.td">{{ stock.attributes.free_real }}</td>
              </tr>
            </template>
          </Datatable>
        </BaseCard>

        <div class="flex justify-end gap-2">
          <BaseButton :disabled="emptyShipQty" @click="confirmVisible = true">Preview</BaseButton>
          <BaseButton color="white" :to="indexRoute">Kembali</BaseButton>
        </div>
      </template>
    </div>

    <ViewOrderModal :preview="previewOrder" :preview-bonus="previewItems" with-bonus :with-log="false" readonly
      :with-payment="false" :with-items="false" label="Kirim Barang Promo" :columns="[
        'code',
        'warehouse',
        'office',
        'date',
        'user',
        'area',
        'shipment_type',
        'address',
      ]" :order-detail-bonus-columns="[]" :visible="confirmVisible" @close="confirmVisible = false">
      <template #actions>
        <BaseButton @click="onSave">
          <Icon icon="heroicons:shield-check-solid" />
          Simpan
        </BaseButton>
        <BaseButton color="white" @click="confirmVisible = false">Kembali</BaseButton>
      </template>
    </ViewOrderModal>
  </BaseWrapper>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import SearchOfficeInput from '../../components/office/search-office-input.vue';
import { requestMixin } from '../../mixins/request/request.js';
import SearchWarehouseInput from '../../components/warehouse/search-warehouse-input.vue';
import BaseRadio from '../../components/base/BaseRadio.vue';
import CreateOrderShipmentAddress from '../../components/order/create-order-shipment-address.vue';
import ViewOrderModal from '../../components/order/view-order-modal.vue';

export default {
  mixins: [requestMixin],
  components: {
    SearchOfficeInput,
    SearchWarehouseInput,
    CreateOrderShipmentAddress,
    ViewOrderModal,
    BaseRadio
  },
  props: {
    indexRoute: {
      type: null,
      required: true
    },
    officeType: {
      type: String,
      required: true
    },
    useOriginWarehouse: Boolean
  },
  data() {
    return {
      confirmVisible: false,
      loading: false,
      selectAddressModalVisible: false,
      customAddressModalVisible: false,
      search: null,
      form: {
        customShipmentAddress: false,
        notes: null,
        office: {
          id: null,
          code: null,
          searchCode: null,
          originalCode: null,
          name: null,
          phone: null,
          hasAddress: false,
          address: null,
        },
        warehouse: {
          id: null,
          code: null,
          name: null,
          searchCode: null,
          originalCode: null,
        },
        order_shipment: 'pickup',
        shipmentAddress: {
          province: {
            id: null,
            name: null
          },
          city: {
            id: null,
            name: null
          },
          district: {
            id: null,
            name: null
          },
          village: {
            id: null,
            name: null
          },
          address: null,
          area: {
            id: null,
            code: null
          },
          receipt_name: null,
          receipt_phone: null,
          uplineAddress: null
        }
      },
      stocks: {
        data: [],
        included: []
      },
      stockShipQty: {}
    };
  },
  computed: {
    ...mapGetters({
      me: 'auth/getUser'
    }),
    officeTypeLabel() {
      return {
        stockist: 'Stockist',
        stockist_center: 'Stockist Center',
        member: 'Mitra Usaha'
      }[this.officeType]
    },
    emptyShipQty() {
      return !Object.values(this.stockShipQty).some(item => item.qty && Number(item.qty.replace(/\D/gi, '')) > 0)
    },
    displayStocks() {
      if (!this.search) {
        return this.stocks.data
      }

      return this.stocks.data.filter(stock => {
        if (stock.attributes.product_code.toLowerCase().includes(this.search.toLowerCase())) {
          return true
        }

        return false
      })
    },
    previewOrder() {
      return {
        data: {
          attributes: {
            order_status: 'Draft',
            origin_code: '-',
            destination_code: '-',
            origin_office_code: 'PUSAT',
            origin_warehouse_code: this.form.warehouse.code,
            createdAt: new Date,
            area_name: this.form.shipmentAddress.area.code,
            area_code: this.form.shipmentAddress.area.code,
            order_shipment: this.form.order_shipment,
            notes: this.form.notes
          },
          relationships: {
            'origin-office': {
              data: null,
            },
            'destination-office': {
              data: {
                type: 'offices',
                id: 1
              },
            },
            'order-details': {
              data: [],
            },
            'order-detail-bonuses': {
              data: [],
            },
            payments: {
              data: [],
            },
            'stock-movements': {
              data: [],
            },
          },
        },
        included: [
          {
            data: {
              type: 'offices',
              id: 1,
              attributes: {
                code: this.form.office.code,
                name: this.form.office.name
              }
            }
          }
        ],
      }
    },
    previewItems() {
      const shipped = Object.entries(this.stockShipQty)
        .filter(([, ship]) => ship.qty && Number(ship.qty.replace(/\D/gi, '')) > 0)
        .map(([stockId, ship]) => {
          const item = this.stocks.data.find(stockItem => stockItem.id === stockId)

          const qty = ship.qty ? Number(ship.qty.replace(/\D/gi, '')) : 0

          return {
            attributes: {
              product_code: item.attributes.product_code,
              product_name: item.attributes.product_code,
              free_follow_up_qty: qty,
              regular_stock_qty: qty,
              bonus_shipped_qty: qty,
              bonus_qty: qty
            }
          }
        })

      return {
        data: shipped,
        meta: {
          page: {},
        },
      }
    }
  },
  methods: {
    ...mapActions({
      createAlert: 'alert/createAlert'
    }),
    async loadOriginWarehouse() {
      this.loading = true

      const [res, err] = await this.request(`/api/v1/warehouses/${this.me.current_warehouse}`);

      if (!err) {
        this.form.warehouse.id = res.data.id
        this.form.warehouse.code = res.data.attributes.code
        this.form.warehouse.name = res.data.attributes.name
        this.form.warehouse.searchCode = res.data.attributes.code
        this.form.warehouse.originalCode = res.data.attributes.code
      }

      this.loadOfficeStock()
    },
    async loadOfficeStock() {
      if (this.useOriginWarehouse || (this.form.office?.id && this.form.warehouse?.id)) {
        this.loading = true;

        const [res, err] = await this.request(`/api/v1/warehouses/${this.form.warehouse.id}/stocks`, {
          params: {
            'filter[has_stock_free_real]': true,
            'filter[not_office_bundle_id]': this.form.office?.id
          }
        });

        if (!err) {
          this.search = null
          this.stocks = res;

          this.stockShipQty = Object.fromEntries(this.stocks.data.map(item => [item.id, { qty: 0, originalQty: 0, stock: item.attributes.free_real }]))
        }

        this.loading = false;
      }
    },
    onChangeOffice(params = {}) {
      const { addresses = [], address = null } = params;

      if (addresses.length || address) {
        const uplineAddress = address || params.addresses[0];

        this.form.customShipmentAddress = true;

        this.form.shipmentAddress.receipt_name = uplineAddress.attributes.name;
        this.form.shipmentAddress.receipt_phone = uplineAddress.attributes.phone;

        this.form.shipmentAddress.province.id = uplineAddress.attributes.province_id;
        this.form.shipmentAddress.province.name =
          uplineAddress.attributes.province_name;

        this.form.shipmentAddress.city.id = uplineAddress.attributes.city_id;
        this.form.shipmentAddress.city.name = uplineAddress.attributes.city_name;

        this.form.shipmentAddress.district.id = uplineAddress.attributes.district_id;
        this.form.shipmentAddress.district.name =
          uplineAddress.attributes.district_name;

        this.form.shipmentAddress.village.id = uplineAddress.attributes.village_id;
        this.form.shipmentAddress.village.name =
          uplineAddress.attributes.village_name;

        this.form.shipmentAddress.uplineAddress = uplineAddress.attributes.detail;
        this.form.shipmentAddress.area.id = `${uplineAddress.attributes.area_uuid}`;
        this.form.shipmentAddress.area.code = uplineAddress.attributes.area_code;

        this.selectAddressModalVisible = false;
      } else {
        this.form.customShipmentAddress = false;

        this.form.shipmentAddress.receipt_name = null;
        this.form.shipmentAddress.receipt_phone = null;

        this.form.shipmentAddress.province.id = null;
        this.form.shipmentAddress.province.name =
          null;

        this.form.shipmentAddress.city.id = null;
        this.form.shipmentAddress.city.name = null;

        this.form.shipmentAddress.district.id = null;
        this.form.shipmentAddress.district.name =
          null;

        this.form.shipmentAddress.village.id = null;
        this.form.shipmentAddress.village.name =
          null;

        this.form.shipmentAddress.uplineAddress = null;
        this.form.shipmentAddress.area.id = null;
        this.form.shipmentAddress.area.code = null;
      }
    },
    onResetShipmentAddress() {
      this.form.customShipmentAddress = false;

      this.form.shipmentAddress.receipt_name = null;
      this.form.shipmentAddress.receipt_phone = null;

      this.form.shipmentAddress.province.id = null;
      this.form.shipmentAddress.province.name = null;

      this.form.shipmentAddress.city.id = null;
      this.form.shipmentAddress.city.name = null;

      this.form.shipmentAddress.district.id = null;
      this.form.shipmentAddress.district.name = null;

      this.form.shipmentAddress.village.id = null;
      this.form.shipmentAddress.village.name = null;

      this.form.shipmentAddress.address = null;
      this.form.shipmentAddress.area.id = null;
      this.form.shipmentAddress.area.code = null;
    },
    onSelectAddress(address) {
      this.form.customShipmentAddress = true;

      this.form.shipmentAddress.receipt_name = address.attributes.name;
      this.form.shipmentAddress.receipt_phone = address.attributes.phone;

      this.form.shipmentAddress.province.id = address.attributes.province_id;
      this.form.shipmentAddress.province.name =
        address.attributes.province_name;

      this.form.shipmentAddress.city.id = address.attributes.city_id;
      this.form.shipmentAddress.city.name = address.attributes.city_name;

      this.form.shipmentAddress.district.id = address.attributes.district_id;
      this.form.shipmentAddress.district.name =
        address.attributes.district_name;

      this.form.shipmentAddress.village.id = address.attributes.village_id;
      this.form.shipmentAddress.village.name = address.attributes.village_name;

      this.form.shipmentAddress.address = address.attributes.detail;
      this.form.shipmentAddress.area.id = `${address.attributes.area_uuid}`;
      this.form.shipmentAddress.area.code = address.attributes.area_code;

      this.selectAddressModalVisible = false;
    },
    onChangeShipmentAddress(address) {
      this.form.customShipmentAddress = true;

      this.form.office.hasAddress = true

      this.form.shipmentAddress.receipt_name = address.data.attributes.name;
      this.form.shipmentAddress.receipt_phone =
        address.data.attributes.phone;

      this.form.shipmentAddress.province.id =
        address.data.attributes.province_id;
      this.form.shipmentAddress.province.name =
        address.data.attributes.province_name;

      this.form.shipmentAddress.city.id = address.data.attributes.city_id;
      this.form.shipmentAddress.city.name = address.data.attributes.city_name;

      this.form.shipmentAddress.district.id =
        address.data.attributes.district_id;
      this.form.shipmentAddress.district.name =
        address.data.attributes.district_name;

      this.form.shipmentAddress.village.id =
        address.data.attributes.village_id;
      this.form.shipmentAddress.village.name =
        address.data.attributes.village_name;

      this.form.shipmentAddress.address = address.data.attributes.detail;
      this.form.shipmentAddress.area.id = `${address.data.attributes.area_uuid}`;
      this.form.shipmentAddress.area.code = address.data.attributes.area_code;

      this.selectAddressModalVisible = false;
    },
    onChangeShipQty(stockId) {
      const ship = this.stockShipQty[stockId]
      const qty = Number(ship.qty.replace(/\D/gi, ''))

      if (qty > ship.stock) {
        this.stockShipQty[stockId].qty = this.stockShipQty[stockId].originalQty
      } else {
        this.stockShipQty[stockId].originalQty = this.stockShipQty[stockId].qty
      }
    },
    async onSave() {
      this.loading = true

      const [, err] = await this.request(`/api/v1/orders/-actions/transfer-stock-promo-regular`, {
        method: 'post',
        data: {
          office_id: this.form.office.id,
          notes: this.form.notes,
          warehouse_id: this.form.warehouse.id,
          order_shipment: this.form.order_shipment,
          ...(this.form.customShipmentAddress
            ? {
              receipt_name: this.form.shipmentAddress.receipt_name,
              receipt_phone: this.form.shipmentAddress.receipt_phone,
              destination_address: {
                province_id: this.form.shipmentAddress.province.id,
                city_id: this.form.shipmentAddress.city.id,
                district_id: this.form.shipmentAddress.district.id,
                village_id: this.form.shipmentAddress.village.id,
                address: this.form.shipmentAddress.address,
              },
            }
            : {}),
          bonuses: Object.entries(this.stockShipQty)
            .map(([stock_id, data]) => ({
              stock_id,
              qty: data.qty ? Number(data.qty.replace(/\D/gi, '')) : null
            }))
            .filter(item => item.qty > 0)
        }
      })

      if (err) {
        this.createAlert({
          status: 'error',
          data: this.getRequestErrorMessage(err)
        })
      } else {
        this.createAlert({
          status: 'success',
          data: 'Stok promo berhasil dikirim'
        })

        this.$router.push(this.indexRoute)
      }

      this.loading = false
    }
  },
  created() {
    if (this.useOriginWarehouse) {
      this.loadOriginWarehouse()
    }
  }
}
</script>